import {createPortal, useFrame, useLoader} from "@react-three/fiber";
import * as THREE from "three";
import React, {useEffect, useMemo, useRef } from "react";
import {Html, OrbitControls, useFBO} from "@react-three/drei";
import gsap from "gsap";
import Scene from "./Scene";
import styles from "./Styles.module.scss";
import {EffectComposer} from "@react-three/postprocessing";
import {TransitionEffect} from "../effects/TransitionEffect";
import {HelicopterIcon, ParcGuell} from "../assets/Icons";

function SceneRenderer({
        textures,
        isTransitioning,
        setIsTransitioning,
        activeId,
        handleTransitionClick,
        showInfo,
        setActiveSceneId,
        nextSceneId,
    }) {
    const [texture1, texture2] = useLoader(THREE.TextureLoader, textures);

    const transitionEffect = useRef();
    const fbo = useFBO();
    const virtualScene = useMemo(() => new THREE.Scene(), []);

    const nextTexture = activeId === 1 ? texture2 : texture1;
    const activeTexture = activeId === 1 ? texture1 : texture2;

    useEffect(() => {
        if (isTransitioning) {
            const transitionParams = {progress: 0.0};
            gsap.to(transitionParams, {
                progress: 1.0,
                duration: 2.5,
                ease: 'power2.inOut',
                onUpdate: () => {
                    if (transitionEffect.current) {
                        transitionEffect.current.uniforms.get('uProgress').value = transitionParams.progress;
                    }
                },
                onComplete: () => {
                    setActiveSceneId(nextSceneId);
                    setIsTransitioning(false);
                }
            });
        }
    }, [isTransitioning]);

    useFrame(({gl, camera}) => {
        gl.setRenderTarget(fbo);
        gl.clear();
        gl.render(virtualScene, camera);
        gl.setRenderTarget(null);
    });

    return (
        <>
            <Scene texture={activeTexture}/>

            {createPortal(<Scene texture={nextTexture}/>, virtualScene)}

            <OrbitControls enableZoom={false} enablePan={false} rotateSpeed={-0.4}/>

            {!isTransitioning &&
                <Html position={activeId === 1 ? [-30, 5, -100] : [-100, 20, -100]}>
                    <div className={styles.pinContainer}>
                        {activeId === 1
                            ? (<ParcGuell onClick={() => handleTransitionClick(2)} className={styles.pin}/>)
                            : (<HelicopterIcon onClick={() => handleTransitionClick(1)} className={styles.pin}/>)
                        }
                    </div>

                </Html>
            }
            {!isTransitioning &&
                <Html position={activeId === 1 ? [-100, 5, -100] : [-15, 20, -100]}>
                    <div onClick={showInfo} className={styles.info}>
                        <span className={styles.infoButton} style={{
                            zIndex: -1,
                            fontSize: '30px',
                            fontWeight: '700',
                            height: '100%',
                            width: '100%'
                        }}>i</span>
                        <span className={styles.infoPulse}></span>
                    </div>
                </Html>
            }

            {isTransitioning && (
                <EffectComposer frameBufferType={THREE.UnsignedByteType}>
                    <TransitionEffect
                        ref={transitionEffect}
                        textureTo={fbo.texture}
                    />
                </EffectComposer>
            )}
        </>
    );
}

export default SceneRenderer;